/**
 * Person Image Configuration
 * 
 * Image naming convention: {gender}-{ageGroup}-{bmiCategory}.svg
 * Images stored in: S3 bucket at read-public/health-span-card/person/
 * 
 * Gender: male, female
 * 
 * Age Groups (fallback when metadata unavailable):
 * - Male: 18-29, 30-39, 40-59, 60-74, 75-plus
 * - Female: 18-29, 30-39, 40-49, 50-59, 60-74, 75-plus
 * 
 * BMI Categories:
 * - underweight: <18.5
 * - normal: 18.5-22.9
 * - overweight: 23.0-24.9
 * - obese1: 25.0-29.9
 * - obese2: ≥30.0
 */

import config from '../../../../config';

const BASE_PATH = `${config.S3_URL}/read-public/health-span-card/person`;
const BG_PATH = `${config.S3_URL}/read-public/health-span-card/bg`;

const MALE_AGE_GROUPS = ['18-29', '30-39', '40-59', '60-74', '75-plus'];
const FEMALE_AGE_GROUPS = ['18-29', '30-39', '40-49', '50-59', '60-74', '75-plus'];
const BMI_CATEGORIES = ['underweight', 'normal', 'overweight', 'obese1', 'obese2'];

/**
 * Get BMI category based on BMI value
 * @param {number} bmi - BMI value
 * @returns {string} BMI category key
 */
const getBmiCategory = (bmi) => {
  if (bmi < 18.5) return 'underweight';
  if (bmi < 23) return 'normal';
  if (bmi < 25) return 'overweight';
  if (bmi < 30) return 'obese1';
  return 'obese2';
};

/**
 * Fallback: Get age group based on age and gender (hardcoded)
 * @param {number} age - Age in years
 * @param {string} gender - 'male' or 'female'
 * @returns {string} Age group key
 */
const getAgeGroupFallback = (age, gender) => {
  if (age < 18) return '18-29';

  if (gender === 'male') {
    if (age <= 29) return '18-29';
    if (age <= 39) return '30-39';
    if (age <= 59) return '40-59';
    if (age <= 74) return '60-74';
    return '75-plus';
  } else {
    if (age <= 29) return '18-29';
    if (age <= 39) return '30-39';
    if (age <= 49) return '40-49';
    if (age <= 59) return '50-59';
    if (age <= 74) return '60-74';
    return '75-plus';
  }
};

/**
 * Resolve age group info from metadata
 * @param {number} age - Age in years
 * @param {string} gender - 'male' or 'female'
 * @param {Object|null} ageGroupMetadata - metadata from API (health_span_age_group)
 * @returns {{ key: string, label: string }}
 */
export const resolveAgeGroup = (age, gender, ageGroupMetadata = null) => {
  const normalizedGender = gender?.toLowerCase() === 'female' ? 'female' : 'male';
  const safeAge = age || 30;

  // Try metadata first
  const groups = ageGroupMetadata?.[normalizedGender]?.age_groups;
  if (groups && Array.isArray(groups) && groups.length > 0) {
    const matched = groups.find(
      (g) => safeAge >= g.age_from && safeAge <= g.age_to
    ) || groups[groups.length - 1];
    return { key: matched.key, label: matched.label };
  }

  // Fallback to hardcoded
  const key = getAgeGroupFallback(safeAge, normalizedGender);
  return { key, label: '' };
};

/**
 * Get person background image path based on gender
 * @param {string} gender - 'male' or 'female'
 * @returns {string} Background image path
 */
export const getPersonBgImage = (gender) => {
  const normalizedGender = gender?.toLowerCase() === 'female' ? 'female' : 'male';
  return `${BG_PATH}/bg-${normalizedGender}.svg`;
};

/**
 * Get person image path based on age, gender, BMI, and optional metadata
 * @param {Object} params - Parameters
 * @param {number} params.age - Age in years
 * @param {string} params.gender - 'male' or 'female'
 * @param {number} params.bmi - BMI value
 * @param {Object|null} params.ageGroupMetadata - metadata from API
 * @returns {string} Image path
 */
export const getPersonImage = ({ age, gender, bmi, ageGroupMetadata = null }) => {
  const normalizedGender = gender?.toLowerCase() === 'female' ? 'female' : 'male';
  const { key: ageGroup } = resolveAgeGroup(age, normalizedGender, ageGroupMetadata);
  const bmiCategory = getBmiCategory(bmi || 22);

  return `${BASE_PATH}/${normalizedGender}-${ageGroup}-${bmiCategory}.svg`;
};

/**
 * Get all image paths for a specific gender
 * Useful for preloading images
 * @param {string} gender - 'male' or 'female'
 * @returns {string[]} Array of image paths
 */
export const getAllImagesForGender = (gender) => {
  const ageGroups = gender === 'female' ? FEMALE_AGE_GROUPS : MALE_AGE_GROUPS;
  const images = [];

  ageGroups.forEach((ageGroup) => {
    BMI_CATEGORIES.forEach((bmiCategory) => {
      images.push(`${BASE_PATH}/${gender}-${ageGroup}-${bmiCategory}.svg`);
    });
  });

  return images;
};

export default {
  getPersonImage,
  getPersonBgImage,
  resolveAgeGroup,
  getAllImagesForGender,
  BASE_PATH,
  BG_PATH,
  MALE_AGE_GROUPS,
  FEMALE_AGE_GROUPS,
  BMI_CATEGORIES,
};
